import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { VirtualSelectField } from "@/components/common/VirtualSelectField";
import type { PaginatedResponse } from "@/types/common";

export interface EntitySelectProps<TItem> {
  queryKeyBase: unknown[];
  fetchPage: (params: {
    page: number;
    limit: number;
    search: string;
  }) => Promise<PaginatedResponse<TItem>>;
  getOptionValue: (item: TItem) => string;
  getOptionLabel: (item: TItem) => string;

  value: string | null | undefined;
  onChange: (value: string, item: TItem) => void;
  onClear?: () => void;

  /**
   * Controlled label override. When omitted, the component remembers the label
   * of the last option selected through it — needed because on-demand loading
   * means the selected item may not be in the currently loaded pages. Pass this
   * when hydrating an existing value (e.g. editing a record) so the trigger
   * shows a name instead of the raw id.
   */
  displayValue?: string;

  /**
   * Resolve a single item by its option value. Supply this and a hydrated value
   * (e.g. a `defaultCompanyId` handed in by the caller) renders as its name
   * without every call site having to thread a `displayValue` through. Only
   * consulted when the value was not chosen through this component and no
   * `displayValue` was given.
   */
  fetchItem?: (value: string) => Promise<TItem | null>;

  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  triggerClassName?: string;
  pageSize?: number;
}

/**
 * Thin generic over {@link VirtualSelectField}: the one searchable, paginated,
 * load-on-demand select. Owns the query wiring and the selected-label
 * bookkeeping so per-entity prefabs (CompanySelect, RecipeSelect, …) stay terse.
 */
export function EntitySelect<TItem>({
  queryKeyBase,
  fetchPage,
  getOptionValue,
  getOptionLabel,
  value,
  onChange,
  onClear,
  displayValue,
  fetchItem,
  ...rest
}: EntitySelectProps<TItem>) {
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>();

  const handleChange = (val: string, item: TItem) => {
    setSelectedLabel(getOptionLabel(item));
    onChange(val, item);
  };

  const effectiveValue = value ?? "";
  const needsResolution = !!effectiveValue && !displayValue && !selectedLabel && !!fetchItem;

  const { data: resolvedItem } = useQuery({
    queryKey: [...queryKeyBase, "resolve", effectiveValue],
    queryFn: () => fetchItem!(effectiveValue),
    enabled: needsResolution,
    staleTime: 5 * 60 * 1000,
  });

  // `selectedLabel` wins over the resolved one so picking an option updates the
  // trigger immediately rather than waiting on a fetch for the new value.
  const effectiveDisplay = effectiveValue
    ? displayValue ?? selectedLabel ?? (resolvedItem ? getOptionLabel(resolvedItem) : undefined)
    : undefined;

  return (
    <VirtualSelectField<TItem>
      queryKeyBase={queryKeyBase}
      buildQueryFn={(search) => (page, size) =>
        fetchPage({ page, limit: size, search })
      }
      getItemValue={getOptionValue}
      getItemLabel={getOptionLabel}
      value={effectiveValue}
      displayValue={effectiveDisplay}
      onChange={handleChange}
      onClear={onClear}
      {...rest}
    />
  );
}
